/**
 * Where every label goes on the page, and where the barcode and the text go on
 * a label. Pure geometry in PDF points (1/72 in), unit-tested; pdf.ts only
 * draws what this computes.
 *
 * Coordinates follow PDF: the origin is the page's bottom-left corner and y
 * grows upward. Labels are numbered the way the sheet is read — across the top
 * row, then down — so label 1 is top-left and label 30 bottom-right.
 */

/** PDF points per inch. */
export const PT = 72;

export type LabelStockId = 'sheet-30up' | 'roll-30336';

/** One label stock: its page, its grid of labels, and the bar width that suits it. */
export interface LabelStock {
  id: LabelStockId;
  pageWidth: number;
  pageHeight: number;
  labelWidth: number;
  labelHeight: number;
  columns: number;
  rows: number;
  /** From the page's left edge to the first column's left edge. */
  marginLeft: number;
  /** From the page's top edge to the first row's top edge. */
  marginTop: number;
  /** Left edge to left edge of adjacent columns, and top to top of adjacent rows. */
  pitchX: number;
  pitchY: number;
  /**
   * The bar module (the narrowest bar) to use when the symbol fits at it. Chosen
   * as a whole number of printer dots so a bar is never rounded to a different
   * width than its neighbor: see each stock below.
   */
  moduleWidth: number;
}

/**
 * 30 labels on a US Letter sheet, the Avery 5160 geometry: 3 across by 10 down,
 * each 2.625 x 1 in, a 0.5 in top margin, a 0.1875 in side margin, columns 2.75 in
 * apart (a 0.125 in gutter), rows 1 in apart (no gutter).
 *
 * The bar module is 1/75 in (0.96 pt, 13.3 mil): exactly 8 dots on a 600 dpi
 * laser and 4 on a 300 dpi one.
 */
export const SHEET_30UP: LabelStock = {
  id: 'sheet-30up',
  pageWidth: 8.5 * PT,
  pageHeight: 11 * PT,
  labelWidth: 2.625 * PT,
  labelHeight: 1 * PT,
  columns: 3,
  rows: 10,
  marginLeft: 0.1875 * PT,
  marginTop: 0.5 * PT,
  pitchX: 2.75 * PT,
  pitchY: 1 * PT,
  moduleWidth: PT / 75,
};

/**
 * A label roll, Dymo 30336: 1 x 2-1/8 in, one label per page, the page being the
 * label itself (2.125 in wide, 1 in tall).
 *
 * The bar module is 1/100 in (0.72 pt, 10 mil): exactly 3 dots on the
 * LabelWriter's 300 dpi head.
 */
export const ROLL_30336: LabelStock = {
  id: 'roll-30336',
  pageWidth: 2.125 * PT,
  pageHeight: 1 * PT,
  labelWidth: 2.125 * PT,
  labelHeight: 1 * PT,
  columns: 1,
  rows: 1,
  marginLeft: 0,
  marginTop: 0,
  pitchX: 2.125 * PT,
  pitchY: 1 * PT,
  moduleWidth: PT / 100,
};

/** How many labels one page of the stock holds. */
export function labelsPerPage(stock: LabelStock): number {
  return stock.columns * stock.rows;
}

/**
 * The first label position to print on, 1-based, clamped to the page: 1 for a
 * fresh sheet, 7 to skip the six labels already peeled off a used one. Anything
 * that is not a whole number in range is 1.
 */
export function clampStartAt(stock: LabelStock, startAt: number): number {
  if (!Number.isInteger(startAt)) return 1;
  return Math.min(Math.max(startAt, 1), labelsPerPage(stock));
}

export interface LabelSlot {
  /** The page, counting from 0. */
  page: number;
  /** The column and row on that page, counting from 0 at the top-left. */
  column: number;
  row: number;
  /** The label's bottom-left corner, in points from the page's bottom-left. */
  x: number;
  y: number;
}

/**
 * Where the nth label to print lands (n counting from 1), given the position on
 * the first sheet to start at. Only the first page is offset: the second page
 * starts at its own label 1.
 */
export function labelSlot(stock: LabelStock, n: number, startAt = 1): LabelSlot {
  const perPage = labelsPerPage(stock);
  const position = n - 1 + (clampStartAt(stock, startAt) - 1);
  const page = Math.floor(position / perPage);
  const onPage = position % perPage;
  const row = Math.floor(onPage / stock.columns);
  const column = onPage % stock.columns;
  return {
    page,
    column,
    row,
    x: stock.marginLeft + column * stock.pitchX,
    y: stock.pageHeight - stock.marginTop - row * stock.pitchY - stock.labelHeight,
  };
}

/** How many pages a job of `labelCount` labels takes; 0 for an empty job. */
export function pageCount(stock: LabelStock, labelCount: number, startAt = 1): number {
  if (labelCount <= 0) return 0;
  return labelSlot(stock, labelCount, startAt).page + 1;
}

// ---------------------------------------------------------------------------
// One label's contents
// ---------------------------------------------------------------------------

/** The clear space kept on each side of the bars: 0.25 in, well over the standard's 10 modules. */
export const MIN_QUIET_ZONE = 0.25 * PT;
/** The narrowest bar module ever drawn: 7.5 mil, barely more than two dots of a 300 dpi printer. */
export const MIN_MODULE_WIDTH = 0.0075 * PT;

/** The label's rows, measured down from its top edge. */
const BARS_TOP = 8;
const BARS_HEIGHT = 26;
const FNSKU_BASELINE = 43;
const TITLE_BASELINE = 52;
const CONDITION_BASELINE = 60.5;
/** Text never comes closer than this to the label's left or right edge. */
const TEXT_MARGIN = 9;

export const FNSKU_FONT_SIZE = 8;
export const TITLE_FONT_SIZE = 6.5;
export const CONDITION_FONT_SIZE = 6.5;

/** The condition line. Every unit this app labels is new; see docs/labels.md. */
export const CONDITION_TEXT = 'New';

/** Where a label's parts sit, in points from the label's bottom-left corner. */
export interface LabelLayout {
  /** The width of one bar module. */
  moduleWidth: number;
  /** The left edge of the first bar. */
  barsX: number;
  /** The bottom edge and height of every bar. */
  barsY: number;
  barsHeight: number;
  /** The clear space actually left on each side of the bars. */
  quietZone: number;
  /** Baselines of the three centered text lines. */
  fnskuBaseline: number;
  titleBaseline: number;
  conditionBaseline: number;
  /** The widest a text line may be, and the label's horizontal center. */
  textWidth: number;
  centerX: number;
}

/**
 * Lays one label out for a symbol `modules` wide. The bars are centered and
 * drawn at the stock's module width when that leaves the quiet zone on both
 * sides; a longer symbol is narrowed just enough to fit, never below
 * {@link MIN_MODULE_WIDTH}. Returns null when even that does not fit — the
 * label is then not printable on this stock rather than printed unreadable.
 */
export function layoutLabel(stock: LabelStock, modules: number): LabelLayout | null {
  if (modules <= 0) return null;
  const available = stock.labelWidth - 2 * MIN_QUIET_ZONE;
  const moduleWidth = Math.min(stock.moduleWidth, available / modules);
  // A hair of tolerance so a symbol that fits exactly is not lost to rounding.
  if (moduleWidth < MIN_MODULE_WIDTH - 1e-9) return null;
  const barsWidth = modules * moduleWidth;
  const barsX = (stock.labelWidth - barsWidth) / 2;
  return {
    moduleWidth,
    barsX,
    barsY: stock.labelHeight - BARS_TOP - BARS_HEIGHT,
    barsHeight: BARS_HEIGHT,
    quietZone: barsX,
    fnskuBaseline: stock.labelHeight - FNSKU_BASELINE,
    titleBaseline: stock.labelHeight - TITLE_BASELINE,
    conditionBaseline: stock.labelHeight - CONDITION_BASELINE,
    textWidth: stock.labelWidth - 2 * TEXT_MARGIN,
    centerX: stock.labelWidth / 2,
  };
}
